import React, { useState, useRef, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Chip,
  Stack,
  Tooltip,
} from "@mui/material";
import {
  Search,
  Download,
  CalendarToday,
  ArrowBack,
  TrendingUp,
  AccountBalanceWallet,
  PeopleAlt,
  SwapHoriz,
  InfoOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import adminService from "../../redux/admin/adminService";
import { colors } from "../../constants/colors";

interface AssetSummaryRow {
  assetClassName: string;
  transactionCount: number;
  uniqueInvestors: number;
  totalAmount: number;
}

interface TransactionRow {
  name: string;
  aumUserId: string;
  accountType: string;
  assetClassName: string;
  fundName: string;
  orderTypeName: string;
  txnType: string;
  qty: number;
  rate: number;
  amount: number;
  orderStatus: string;
  paymentStatus: string;
  transactionDate: string;
}

interface TransactionsInitiatedData {
  grandTotalAmount: number;
  totalTransactions: number;
  totalInvestors: number;
  assetSummary: AssetSummaryRow[];
  transactions: TransactionRow[];
  startDate: number;
  endDate: number;
}

const ASSET_CLASS_COLORS: Record<string, string> = {
  "Mutual Funds":         "#255288",
  "Other":                "#6B7280",
};

const DATE_PRESETS = [
  { value: "1day",  label: "1D" },
  { value: "7day",  label: "7D" },
  { value: "1month",label: "1M" },
  { value: "1year", label: "1Y" },
  { value: "max",   label: "Max" },
  { value: "custom",label: "Custom" },
];

const formatAmount = (val: number): string => {
  if (!val && val !== 0) return "₹0";
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)} Cr`;
  if (val >= 100000)   return `₹${(val / 100000).toFixed(2)} L`;
  if (val >= 1000)     return `₹${(val / 1000).toFixed(2)} K`;
  return `₹${val.toFixed(2)}`;
};

const STATUS_PREVIEW_LEN = 40;

const formatQty = (val: number): string => {
  if (val === null || val === undefined || Number.isNaN(val)) return "-";
  return Number(val).toLocaleString("en-IN", { maximumFractionDigits: 3 });
};

const formatRate = (val: number): string => {
  if (val === null || val === undefined || Number.isNaN(val)) return "-";
  return Number(val).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const truncateStatus = (text: string): { display: string; truncated: boolean; full: string } => {
  const full = (text || "").trim();
  if (!full) return { display: "-", truncated: false, full: "" };
  if (full.length <= STATUS_PREVIEW_LEN) return { display: full, truncated: false, full };
  const slice = full.slice(0, STATUS_PREVIEW_LEN);
  const lastSpace = slice.lastIndexOf(" ");
  const short = (lastSpace > 15 ? slice.slice(0, lastSpace) : slice).trimEnd();
  return { display: `${short}…`, truncated: true, full };
};

const StatusCell: React.FC<{ value: string }> = ({ value }) => {
  const { display, truncated, full } = truncateStatus(value);
  if (!truncated) {
    return (
      <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
        {display}
      </Typography>
    );
  }
  return (
    <Tooltip
      title={<Typography variant="body2" sx={{ whiteSpace: "pre-wrap", maxWidth: 360 }}>{full}</Typography>}
      arrow
      placement="top"
    >
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, cursor: "help", maxWidth: 220 }}>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {display}
        </Typography>
        <InfoOutlined sx={{ fontSize: 14, color: "text.secondary", flexShrink: 0 }} />
      </Box>
    </Tooltip>
  );
};

const getDateRange = (type: string): { startDate: number; endDate: number } => {
  const now = new Date();
  const endDate = now.getTime();
  let startDate: number;
  switch (type) {
    case "1day":   startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime(); break;
    case "7day":   startDate = endDate - 7  * 24 * 60 * 60 * 1000; break;
    case "1month": startDate = endDate - 30 * 24 * 60 * 60 * 1000; break;
    case "1year":  startDate = endDate - 365 * 24 * 60 * 60 * 1000; break;
    case "max":    startDate = new Date(2000, 0, 1).getTime(); break;
    default:       startDate = endDate - 7 * 24 * 60 * 60 * 1000;
  }
  return { startDate, endDate };
};

const TransactionsInitiated: React.FC = () => {
  const navigate = useNavigate();
  const [dateType, setDateType] = useState<string>("7day");
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<TransactionsInitiatedData | null>(null);
  const [search, setSearch] = useState<string>("");
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);
  const cacheRef = useRef<Record<string, TransactionsInitiatedData>>({});

  const fetchData = useCallback(async (isCustomSubmit = false) => {
    let startDate: number;
    let endDate: number;

    if (dateType === "custom") {
      if (!isCustomSubmit) return;
      if (!customStartDate || !customEndDate) {
        toast.error("Please select both start and end dates.");
        return;
      }
      startDate = new Date(customStartDate).setHours(0, 0, 0, 0);
      endDate   = new Date(customEndDate).setHours(23, 59, 59, 999);
      if (startDate > endDate) {
        toast.error("Start date cannot be after end date.");
        return;
      }
    } else {
      const range = getDateRange(dateType);
      startDate = range.startDate;
      endDate   = range.endDate;
    }

    const cacheKey = dateType === "custom" ? `${customStartDate}-${customEndDate}` : dateType;
    if (cacheRef.current[cacheKey]) {
      setData(cacheRef.current[cacheKey]);
      return;
    }

    setLoading(true);
    try {
      const res = await adminService.getSuperAdminOpsTransactionsInitiated({ startDate, endDate });
      if (res?.statusCode === 200 && res?.data) {
        cacheRef.current[cacheKey] = res.data;
        setData(res.data);
      } else {
        toast.error(res?.message || "Failed to fetch data.");
      }
    } catch {
      toast.error("An error occurred while fetching data.");
    } finally {
      setLoading(false);
    }
  }, [dateType, customStartDate, customEndDate]);

  const handlePresetChange = (preset: string) => {
    setDateType(preset);
    setSelectedAsset(null);
    if (preset !== "custom") {
      const cacheKey = preset;
      if (cacheRef.current[cacheKey]) {
        setData(cacheRef.current[cacheKey]);
        return;
      }
      const range = getDateRange(preset);
      setLoading(true);
      adminService.getSuperAdminOpsTransactionsInitiated(range).then((res) => {
        if (res?.statusCode === 200 && res?.data) {
          cacheRef.current[cacheKey] = res.data;
          setData(res.data);
        } else {
          toast.error(res?.message || "Failed to fetch data.");
        }
      }).catch(() => {
        toast.error("An error occurred while fetching data.");
      }).finally(() => setLoading(false));
    }
  };

  React.useEffect(() => { handlePresetChange("7day"); }, []);

  const handleExport = () => {
    if (!data?.transactions?.length) return;
    const rows = filteredTransactions.map((t) => ({
      "Transaction Date": t.transactionDate,
      "Investor Name":    t.name,
      "User Id":          t.aumUserId,
      "Account Type":     t.accountType,
      "Fund Name":        t.fundName || "-",
      "Invst Type":       t.txnType,
      "Order Type":       t.orderTypeName || "-",
      "Qty":              t.qty,
      "Rate":             t.rate,
      "Value":            t.amount,
      "Order Status":     t.orderStatus || "-",
      "Payment Status":   t.paymentStatus || "-",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transactions Initiated Report");
    XLSX.writeFile(wb, `Txn_Initiated_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const filteredTransactions = (data?.transactions || []).filter((t) => {
    const q = search.toLowerCase();
    const matchSearch =
      !search ||
      (t.name || "").toLowerCase().includes(q) ||
      (t.aumUserId || "").toLowerCase().includes(q) ||
      (t.fundName || "").toLowerCase().includes(q) ||
      (t.orderTypeName || "").toLowerCase().includes(q);
    const matchAsset = !selectedAsset || t.assetClassName === selectedAsset;
    return matchSearch && matchAsset;
  });

  return (
    <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, md: 4 } }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate("/super-admin/ops-dashboard")}
          sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer" }}
        >
          <ArrowBack fontSize="small" /> Ops Dashboard
        </Link>
        <Typography color="text.primary" fontWeight={600}>Transactions Initiated</Typography>
      </Breadcrumbs>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color={colors.primary}>
            Transactions Initiated
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Total volume of all intents/orders submitted by investors on the platform
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleExport}
          disabled={!data?.transactions?.length}
          sx={{ borderColor: colors.primary, color: colors.primary }}
        >
          Export Excel
        </Button>
      </Box>

      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <CalendarToday fontSize="small" sx={{ color: colors.primary }} />
            <Typography variant="subtitle2" fontWeight={600} sx={{ mr: 1 }}>
              Select Range:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {DATE_PRESETS.map((preset) => (
                <Button
                  key={preset.value}
                  variant={dateType === preset.value ? "contained" : "outlined"}
                  size="small"
                  onClick={() => handlePresetChange(preset.value)}
                  sx={{
                    minWidth: 52,
                    borderColor: colors.primary,
                    backgroundColor: dateType === preset.value ? colors.primary : "transparent",
                    color: dateType === preset.value ? "#fff" : colors.primary,
                    "&:hover": { backgroundColor: colors.primary, color: "#fff" },
                  }}
                >
                  {preset.label}
                </Button>
              ))}
            </Stack>

            {dateType === "custom" && (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: { xs: 1, sm: 0 } }}>
                <TextField
                  type="date"
                  size="small"
                  label="From"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 160 }}
                />
                <TextField
                  type="date"
                  size="small"
                  label="To"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 160 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => fetchData(true)}
                  sx={{ backgroundColor: colors.primary }}
                >
                  Apply
                </Button>
              </Stack>
            )}
          </Box>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: colors.primary }} />
        </Box>
      ) : data ? (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ borderRadius: 2, borderLeft: `4px solid ${colors.primary}` }}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Total Initiated Amount</Typography>
                      <Typography variant="h5" fontWeight={700} color={colors.primary}>
                        {formatAmount(data.grandTotalAmount)}
                      </Typography>
                    </Box>
                    <AccountBalanceWallet sx={{ color: colors.primary, fontSize: 36, opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ borderRadius: 2, borderLeft: "4px solid #08991F" }}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Total Requests Submitted</Typography>
                      <Typography variant="h5" fontWeight={700} color="#08991F">
                        {data.totalTransactions.toLocaleString()}
                      </Typography>
                    </Box>
                    <SwapHoriz sx={{ color: "#08991F", fontSize: 36, opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ borderRadius: 2, borderLeft: "4px solid #F17A21" }}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>Unique Requesting Investors</Typography>
                      <Typography variant="h5" fontWeight={700} color="#F17A21">
                        {data.totalInvestors.toLocaleString()}
                      </Typography>
                    </Box>
                    <PeopleAlt sx={{ color: "#F17A21", fontSize: 36, opacity: 0.7 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Initiations by Asset Class
            {selectedAsset && (
              <Chip
                label={`Filtered: ${selectedAsset}`}
                size="small"
                onDelete={() => setSelectedAsset(null)}
                sx={{ ml: 2, backgroundColor: colors.primary, color: "#fff" }}
              />
            )}
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {data.assetSummary.map((asset) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={asset.assetClassName}>
                <Card
                  onClick={() => setSelectedAsset(selectedAsset === asset.assetClassName ? null : asset.assetClassName)}
                  sx={{
                    borderRadius: 2,
                    cursor: "pointer",
                    border: selectedAsset === asset.assetClassName
                      ? `2px solid ${ASSET_CLASS_COLORS[asset.assetClassName] || "#6B7280"}`
                      : "2px solid transparent",
                    transition: "all 0.2s",
                    "&:hover": { boxShadow: 4 },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{ color: ASSET_CLASS_COLORS[asset.assetClassName] || "#6B7280" }}
                      >
                        {asset.assetClassName}
                      </Typography>
                      <TrendingUp sx={{ color: ASSET_CLASS_COLORS[asset.assetClassName] || "#6B7280", fontSize: 20 }} />
                    </Box>
                    <Typography variant="h6" fontWeight={700} color={colors.primary}>
                      {formatAmount(asset.totalAmount)}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {asset.transactionCount} requests
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {asset.uniqueInvestors} investors
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
            {data.assetSummary.length === 0 && (
              <Grid item xs={12}>
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  No initiated transactions found for the selected date range.
                </Typography>
              </Grid>
            )}
          </Grid>

          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
                <Typography variant="h6" fontWeight={600}>
                  Initiation Log Details
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({filteredTransactions.length} records)
                  </Typography>
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search by name, user id, fund or order type..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 280 }}
                />
              </Box>

              <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 500, overflow: "auto" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {["#", "Transaction Date", "Investor Name", "User Id", "Fund Name", "Invst Type", "Order Type", "Qty", "Rate", "Value", "Order Status", "Payment Status"].map((h) => (
                        <TableCell
                          key={h}
                          sx={{ fontWeight: 700, backgroundColor: "#f5f7fa", color: colors.primary }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTransactions.length > 0 ? (
                      filteredTransactions.map((row, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{row.transactionDate}</TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {row.name}
                            <Typography display="block" variant="caption" color="text.secondary">
                              {row.accountType}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={row.aumUserId} size="small" variant="outlined" sx={{ borderColor: colors.primary, color: colors.primary }} />
                          </TableCell>
                          <TableCell sx={{ minWidth: 180, maxWidth: 280 }}>
                            {row.fundName || "-"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.txnType || "Buy"}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                backgroundColor: row.txnType?.toLowerCase() === "sell" ? "#DC262622" : "#08991F22",
                                color: row.txnType?.toLowerCase() === "sell" ? "#DC2626" : "#08991F",
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.orderTypeName || "-"}
                              size="small"
                              sx={{ fontWeight: 600, backgroundColor: "#25528822", color: "#255288" }}
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{formatQty(row.qty)}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{formatRate(row.rate)}</TableCell>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>{formatAmount(row.amount)}</TableCell>
                          <TableCell><StatusCell value={row.orderStatus} /></TableCell>
                          <TableCell><StatusCell value={row.paymentStatus} /></TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={12} align="center" sx={{ py: 4, color: "text.secondary" }}>
                          No request logs found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      ) : (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography color="text.secondary">Select a date range to load initiation data.</Typography>
        </Box>
      )}
    </Container>
  );
};

export default TransactionsInitiated;
